import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Container } from '@plone/components';
import cx from 'classnames';
import Logo from '@ifpb/portal/components/Logo/Logo';
import SearchWidget from '@plone/volto/components/theme/SearchWidget/SearchWidget';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import SlotRenderer from '@plone/volto/components/theme/SlotRenderer/SlotRenderer';
import { useLiveData } from '@kitconcept/volto-light-theme/helpers/useLiveData';
import Navigation from '@ifpb/portal/components/Navigation/Navigation';
import NavigationButton from '@ifpb/portal/components/Header/NavigationButton';
import type { SiteHeaderSettings } from '@kitconcept/volto-light-theme/types';
import type { Content } from '@plone/types';

type FormState = {
  content: {
    data: Content;
  };
  navroot: {
    data: {
      navroot: Content;
    };
  };
  form: {
    global: Content;
  };
};

const InternetHeader = ({ pathname, content }) => {
  const [isNavMenuOpen, setNavMenuOpen] = useState(false);
  const [resetToRoot, setResetToRoot] = useState(false);
  const [isHeaderActionsMenuOpen, setHeaderActionsMenuOpen] = useState(false);
  const isToggling = useRef(false);

  const toggleNavMenu = useCallback(() => {
    if (isToggling.current) {
      return;
    }

    isToggling.current = true;
    const body = document.getElementsByTagName('body')[0];

    setNavMenuOpen((prevState) => {
      if (prevState) {
        body.classList.remove('has-menu-open');
        return false;
      } else {
        body.classList.add('has-menu-open');
        setResetToRoot(false);
        return true;
      }
    });

    setTimeout(() => {
      isToggling.current = false;
    }, 100);
  }, []);

  const toggleHeaderActionsMenu = useCallback(() => {
    setHeaderActionsMenuOpen((prevState) => !prevState);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isHeaderActionsMenuOpen && !event.target.closest('.header-actions')) {
        setHeaderActionsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isHeaderActionsMenuOpen]);
  const header_actions = useLiveData<SiteHeaderSettings['header_actions']>(
    content,
    'ifpb.portal.header',
    'header_actions',
  );
  const navRoot = useSelector((state) => state.navroot?.data?.navroot) || null;
  return (
    <>
      <div className="header">
        <div className="header-desktop">
          <div className="tools-wrapper">
            <div className="logo">
              <Logo isFooterLogo={false} />
            </div>
            <div className="tools">
              <div className="header-actions">
                {header_actions &&
                  Array.isArray(header_actions) &&
                  header_actions.map((item, index) => (
                    <UniversalLink
                      key={`${item['@id']}-${index}`}
                      href={item.href?.[0]?.['@id']}
                      openLinkInNewTab={item.openInNewTab}
                    >
                      {item.title}
                    </UniversalLink>
                  ))}
                <SlotRenderer name="headerTools" content={content} />
              </div>
            </div>
          </div>
          <div className="nav-wrapper">
            <NavigationButton
              isNavMenuOpen={isNavMenuOpen}
              toggleNavMenu={toggleNavMenu}
            />
            {navRoot && <div className="nav-root-title">{navRoot.title}</div>}
            <div className="search-wrapper navigation-desktop">
              <div className="search">
                <SearchWidget />
              </div>
            </div>
          </div>
        </div>
        {/* Mobile */}
        <div className="header-mobile">
          <div className="tools-wrapper">
            <div className="logo">
              <Logo isFooterLogo={false} />
            </div>
            <div className="tools">
              <div className="header-actions">
                <button
                  className={cx('header-actions-toggle', {
                    active: isHeaderActionsMenuOpen,
                  })}
                  onClick={toggleHeaderActionsMenu}
                  aria-label="Toggle header actions menu"
                >
                  ⋮
                </button>
                {isHeaderActionsMenuOpen && (
                  <div className="header-actions-dropdown">
                    <div className="header-actions-title">Acesso Rápido</div>
                    {header_actions &&
                      Array.isArray(header_actions) &&
                      header_actions.map((item, index) => (
                        <UniversalLink
                          key={`${item['@id']}-${index}`}
                          href={item.href?.[0]['@id']}
                          openLinkInNewTab={item.openInNewTab}
                          onClick={() => setHeaderActionsMenuOpen(false)}
                        >
                          {item.title}
                        </UniversalLink>
                      ))}
                  </div>
                )}
              </div>
              <div className="search-wrapper navigation-mobile">
                <div className="search">
                  <SearchWidget />
                </div>
              </div>
              <SlotRenderer name="headerTools" content={content} />
            </div>
          </div>
          <div className="nav-wrapper">
            <NavigationButton
              isNavMenuOpen={isNavMenuOpen}
              toggleNavMenu={toggleNavMenu}
            />
            {navRoot && <div className="nav-root-title">{navRoot.title}</div>}
          </div>
        </div>
      </div>
      <Navigation
        content={content}
        pathname={pathname}
        isNavMenuOpen={isNavMenuOpen}
        toggleNavMenu={toggleNavMenu}
        resetToRoot={resetToRoot}
        setResetToRoot={setResetToRoot}
      />
    </>
  );
};

const Header = (props) => {
  const { pathname } = props;
  const content = useSelector<FormState, Content>(
    (state) => state.content.data,
    shallowEqual,
  );

  const navRoot = useSelector<FormState, Content>(
    (state) => state.navroot?.data?.navroot,
  );

  return (
    <>
      <SlotRenderer name="aboveHeader" content={content} navRoot={navRoot} />
      <header className={cx('header-wrapper')}>
        <Container layout>
          <InternetHeader pathname={pathname} content={content} />
        </Container>
      </header>
      <SlotRenderer name="belowHeader" content={content} navRoot={navRoot} />
    </>
  );
};

export default Header;
